import { Controller } from "@hotwired/stimulus";

// Client combobox for the schedule quick-create modal.
// Behavior:
//   - Type → debounced fetch to GET /operations/clients/search?q=
//   - Renders matches with name + phone; clicking sets client_id (existing client)
//   - If query has no exact match, surfaces "+ Create <typed>" — clicking
//     clears client_id and uses the typed name as client_name.
//   - Submitting requires either an existing pick or a typed name.
//
// Markup contract — see _quick_form_modal.html.erb:
//   data-controller="quick-client-combobox"
//   data-quick-client-combobox-search-url-value="/operations/clients/search"
//   <input data-quick-client-combobox-target="input">
//   <input data-quick-client-combobox-target="hiddenName" hidden>
//   <input data-quick-client-combobox-target="hiddenId" hidden>
//   <input data-quick-client-combobox-target="hiddenPhone" hidden>  <!-- optional %>
//   <ul data-quick-client-combobox-target="results"></ul>
export default class extends Controller {
  static targets = ["input", "results", "hiddenId", "hiddenName", "hiddenPhone"];
  static values = { searchUrl: String };

  connect() {
    this._onDocumentClick = (e) => {
      if (!this.element.contains(e.target)) this._hide();
    };
    document.addEventListener("click", this._onDocumentClick);
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick);
    clearTimeout(this._debounce);
  }

  onInput() {
    const q = this.inputTarget.value.trim();
    this.hiddenIdTarget.value = "";
    this.hiddenNameTarget.value = q;
    if (this.hasHiddenPhoneTarget) this.hiddenPhoneTarget.value = "";

    clearTimeout(this._debounce);
    if (q.length < 2) {
      this._renderCreateOnly(q);
      return;
    }
    this._debounce = setTimeout(() => this._fetch(q), 200);
  }

  onFocus() {
    if (this.inputTarget.value.trim()) this._show();
  }

  async _fetch(q) {
    try {
      const res = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });
      if (!res.ok) throw new Error();
      const payload = await res.json();
      this._renderResults(q, payload.clients || []);
    } catch (_) {
      this._renderCreateOnly(q);
    }
  }

  _renderResults(q, clients) {
    this.resultsTarget.innerHTML = "";
    const exactMatch = clients.find((c) => c.name?.toLowerCase() === q.toLowerCase());

    clients.forEach((c) => this.resultsTarget.appendChild(this._buildExistingItem(c)));
    if (!exactMatch && q) this.resultsTarget.appendChild(this._buildCreateItem(q));
    this._show();
  }

  _renderCreateOnly(q) {
    this.resultsTarget.innerHTML = "";
    if (q) {
      this.resultsTarget.appendChild(this._buildCreateItem(q));
      this._show();
    } else {
      this._hide();
    }
  }

  _buildExistingItem(client) {
    const li = document.createElement("li");
    li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm";
    li.innerHTML = `
      <div class="font-medium text-black">${this._safe(client.name)}</div>
      <div class="text-xs text-gray-500">${this._safe(client.phone || "")}</div>`;
    li.addEventListener("click", () => this._pickExisting(client));
    return li;
  }

  _buildCreateItem(q) {
    const li = document.createElement("li");
    li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-t border-gray-200";
    li.innerHTML = `<span class="font-medium text-black">+ Create</span> <span class="text-gray-700">${this._safe(q)}</span>`;
    li.addEventListener("click", () => this._pickCreate(q));
    return li;
  }

  _pickExisting(client) {
    this.inputTarget.value = client.name;
    this.hiddenIdTarget.value = client.id;
    this.hiddenNameTarget.value = client.name;
    if (this.hasHiddenPhoneTarget) this.hiddenPhoneTarget.value = client.phone || "";
    this._hide();
  }

  _pickCreate(q) {
    this.inputTarget.value = q;
    this.hiddenIdTarget.value = "";
    this.hiddenNameTarget.value = q;
    this._hide();
  }

  _show() { this.resultsTarget.classList.remove("hidden"); }
  _hide() { this.resultsTarget.classList.add("hidden"); }

  _safe(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
